import { useAuth } from "@/contexts/AuthContext";
import { cn, getInitials } from "@/lib/utils";
import * as shared from "@/styles/shared";
import type { SidebarProps } from "@/types/auth";
import type { AdminNavLinkProps } from "@/types/ui";
import { Role } from "@shared/types/models";
import { LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { iconMap, sectionGroups } from "./Sidebar.constants";
import * as sidebarStyles from "./Sidebar.styles";

interface PillRect {
  top: number;
  height: number;
}

const AdminNavLink = ({ link, onClose, iconMap, styles, t, location }: AdminNavLinkProps) => {
  const IconComponent = iconMap[link.to];
  const isActive = location.pathname === link.to;

  return (
    <NavLink
      key={link.to}
      to={link.to}
      onClick={onClose}
      className={cn(styles.navLinkBase, isActive ? styles.navLinkActive : styles.navLinkInactive)}
    >
      {IconComponent && <IconComponent className={styles.icon} />}
      <span>{t(`nav.${link.labelKey}`)}</span>
    </NavLink>
  );
};

const Sidebar = ({ open, onClose }: SidebarProps) => {
  const { t } = useTranslation("common");
  const { user, logout } = useAuth();
  const location = useLocation();

  const isAdmin = user?.role === Role.ADMIN;

  const filteredLinks = sidebarStyles.links.filter((link) => !link.adminOnly || isAdmin);

  const groupedLinks = sectionGroups.map((group) => ({
    labelKey: group.labelKey,
    links: filteredLinks.filter((link) => group.links.includes(link.to)),
  }));

  const adminLink = filteredLinks.find((link) => link.adminOnly);

  const navRef = useRef<HTMLElement | null>(null);
  const linkRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const [pill, setPill] = useState<PillRect | null>(null);

  useEffect(() => {
    const measure = () => {
      const nav = navRef.current;
      if (!nav) return;
      const el = linkRefs.current[location.pathname];
      if (!el) {
        setPill(null);
        return;
      }
      const navRect = nav.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      setPill({ top: elRect.top - navRect.top + nav.scrollTop, height: elRect.height });
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [location.pathname, open]);

  const setLinkRef = (to: string) => (el: HTMLAnchorElement | null) => {
    linkRefs.current[to] = el;
  };

  return (
    <>
      {open && <div className={sidebarStyles.styles.overlay} onClick={onClose} />}
      <aside className={cn(sidebarStyles.styles.aside, open ? sidebarStyles.styles.asideOpen : sidebarStyles.styles.asideClosed)}>
        <div className={sidebarStyles.styles.header}>
          <Logo onClick={onClose} />
        </div>

        <nav ref={navRef} className={sidebarStyles.styles.nav}>
          <div
            className={cn(sidebarStyles.styles.navPill, !pill && "opacity-0")}
            style={pill ? { top: pill.top, height: pill.height } : undefined}
            aria-hidden="true"
          />
          {groupedLinks.map((group) => (
            <div key={group.labelKey} className={sidebarStyles.styles.mb1}>
              <div className={sidebarStyles.styles.sectionLabel}>{t(group.labelKey)}</div>
              {group.links.map((link) => {
                const IconComponent = iconMap[link.to];
                const isActive = location.pathname === link.to;
                return (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    ref={setLinkRef(link.to)}
                    onClick={onClose}
                    className={cn(
                      sidebarStyles.styles.navLinkBase,
                      isActive ? sidebarStyles.styles.navLinkActive : sidebarStyles.styles.navLinkInactive,
                    )}
                  >
                    {IconComponent && <IconComponent className={sidebarStyles.styles.icon} />}
                    <span>{t(`nav.${link.labelKey}`)}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}

          {adminLink && isAdmin && (
            <AdminNavLink link={adminLink} onClose={onClose} iconMap={iconMap} styles={sidebarStyles.styles} t={t} location={location} />
          )}
        </nav>

        <div className={sidebarStyles.styles.userSection}>
          <div className={sidebarStyles.styles.userContent}>
            <div className={sidebarStyles.styles.avatar}>{getInitials(user?.name ?? null, user?.email ?? "")}</div>
            <div className={shared.flexTruncate}>
              <div className={sidebarStyles.styles.userName}>{user?.name || user?.email || "User"}</div>
              <div className={sidebarStyles.styles.userEmail}>{user?.email}</div>
            </div>
            <button onClick={logout} className={sidebarStyles.styles.logoutBtn} aria-label={t("button.logout")} title={t("button.logout")}>
              <LogOut className={shared.iconSm} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export { Sidebar };
